import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Icon, type IconName } from "./icon";
import { cn } from "@/lib/cn";

const button = cva(
  // Shape lock: 8px radius on every control. Tactile press on all variants.
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap " +
    "transition-[background-color,border-color,color,transform] duration-150 " +
    "active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-accent text-accent-ink hover:bg-accent-hover",
        secondary: "border border-line-strong bg-surface text-ink hover:bg-surface-sunken",
        ghost: "text-ink-soft hover:bg-surface-sunken hover:text-ink",
        danger: "bg-bad text-white hover:brightness-90",
        link: "text-accent underline-offset-4 hover:underline px-0",
      },
      size: {
        sm: "h-8 px-3 text-[13px]",
        md: "h-9 px-4 text-sm",
        lg: "h-11 px-5 text-[15px]",
        /* Square icon-only target. 36px + the surrounding row padding clears the
           44px hit area; standalone icon buttons use `icon-lg`. */
        icon: "h-9 w-9",
        "icon-lg": "h-11 w-11",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
  /** Renders a spinner and blocks input. Label stays put so width doesn't jump. */
  loading?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, loading, iconLeft, iconRight, children, disabled, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";

  /**
   * Slot accepts exactly one element child, so the icons are wrapped around a
   * <Slottable> rather than sitting beside the child. Radix then renders them
   * INSIDE the slotted element (a <Link>, say) instead of throwing. Without
   * this, `asChild` plus an icon crashes the tree at render time.
   */
  const content = (
    <>
      {loading ? (
        <Icon name="spinner" size={16} className="animate-spin" />
      ) : (
        iconLeft && <Icon name={iconLeft} size={16} />
      )}
      {asChild ? <Slottable>{children}</Slottable> : children}
      {iconRight && !loading && <Icon name={iconRight} size={16} />}
    </>
  );

  return (
    <Comp
      ref={ref}
      className={cn(button({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Comp>
  );
});

export { button as buttonVariants };
